import React, { useState, useEffect, useMemo, useRef } from 'react';
import { makeTr } from '../services/trLookup';
import GrenzpaketPanel from '../components/GrenzpaketPanel';
import {
  grenzpaketFreigeschaltet,
  grenzpaketAktiv,
  grenzpaketCustomerSearch,
  extractGrenzpaketFromLabel,
  istGrenzpaketShopStrasse,
  formatGrenzpaketName,
  stripGrenzpaketKundenNrFromName,
  formatGrenzpaketStreet,
  buildGrenzpaketNote,
  parseGrenzpaketMeta,
  getDefaultGrenzpaketType,
  getGrenzpaketTarifDetails,
  istZentraleBuchhaltungsLog,
  isDemoModeActive,
  setDemoModeActive,
  isDemoPackage,
  gt,
} from '../services/grenzpaket';

export { isDemoModeActive, setDemoModeActive, isDemoPackage };

/**
 * 🇨🇭 Grenzpaket-Modul (Konto 1186 & Admin-Test)
 * Kapselt alle Sonderlogiken für Annahme (Kundenabgleich), Suche, Ausgabe (Tarif-Banner)
 * und Einstellungen, sodass der Standard-Core (NewPackageModal, SignatureModal, AddPackageWithAI)
 * frei von kundenspezifischem Code bleibt.
 */
export function useGrenzpaketIntake({
  initialName = '',
  initialStreetAndNumber = '',
  initialNotizen = '',
  name = '',
  setName,
  regalplatz = '',
  setRegalplatz,
  onStopSeries,
  onClearNameInvalid,
  onClearShelfInvalid,
}) {
  const aktiv = grenzpaketAktiv();
  const initialGpMeta = useMemo(
    () => (aktiv ? parseGrenzpaketMeta({ notizen: initialNotizen, streetAndNumber: initialStreetAndNumber }) : null),
    // eslint-disable-next-line
    []
  );

  const [gpKundenNr, setGpKundenNr] = useState(initialGpMeta?.kundenNr || '');
  const [gpPaketTyp, setGpPaketTyp] = useState(() => initialGpMeta?.paketTyp || getDefaultGrenzpaketType());
  const [gpShopInfo, setGpShopInfo] = useState(() => {
    const raw = String(initialGpMeta?.info || '').trim();
    return istZentraleBuchhaltungsLog(raw) ? '' : raw;
  });
  const [gpTrefferListe, setGpTrefferListe] = useState([]);
  const [gpAusgewaehlt, setGpAusgewaehlt] = useState(
    initialGpMeta?.kundenNr
      ? {
          kundenNr: initialGpMeta.kundenNr,
          name: initialName || '',
          tarif: initialGpMeta.tarif || 'Unbekannt',
          postboxTyp: initialGpMeta.postboxTyp || '',
          guthaben: initialGpMeta.guthaben ?? null,
          info: '',
        }
      : null
  );
  const [gpLaedt, setGpLaedt] = useState(false);
  const [gpFehler, setGpFehler] = useState('');
  const gpManualSelectRef = useRef(false);
  const gpAusgewaehltRef = useRef(gpAusgewaehlt);
  gpAusgewaehltRef.current = gpAusgewaehlt;
  const setNameRef = useRef(setName);
  setNameRef.current = setName;

  // Letzte grobe Vorsortierung (Vorne / Hinten / Später / Sperrgut) für das nächste Paket merken
  const presortInitRef = useRef(false);
  useEffect(() => {
    if (!aktiv || presortInitRef.current) return;
    presortInitRef.current = true;
    if (!initialName && !initialStreetAndNumber && !initialNotizen && !regalplatz && setRegalplatz) {
      try {
        const lastPresort = localStorage.getItem('kiosk_gp_last_presort');
        if (lastPresort && ['Vorne', 'Hinten', 'Sperrgut'].includes(lastPresort)) {
          setRegalplatz(lastPresort);
        }
      } catch { /* ignore */ }
    }
  }, [aktiv, initialName, initialStreetAndNumber, initialNotizen, regalplatz, setRegalplatz]);

  useEffect(() => {
    if (!aktiv) return undefined;
    const kdClean = String(gpKundenNr || '').replace(/\D+/g, '').trim();
    const nameClean = stripGrenzpaketKundenNrFromName(name);
    const aktKunde = gpAusgewaehltRef.current;
    if (
      aktKunde &&
      (!kdClean || aktKunde.kundenNr === kdClean) &&
      (!nameClean || stripGrenzpaketKundenNrFromName(aktKunde.name).toLowerCase() === nameClean.toLowerCase()) &&
      (gpManualSelectRef.current || (aktKunde.kundenNr && String(name || '').includes(aktKunde.kundenNr)))
    ) {
      setGpLaedt(false);
      return undefined;
    }
    if (kdClean.length < 3 && nameClean.length < 2) {
      setGpLaedt(false);
      setGpTrefferListe([]);
      if (!kdClean) setGpAusgewaehlt(null);
      setGpFehler('');
      return undefined;
    }
    let abgebrochen = false;
    const timer = setTimeout(async () => {
      setGpLaedt(true);
      setGpFehler('');
      const res = await grenzpaketCustomerSearch({
        kundenNr: kdClean.length >= 3 ? kdClean : '',
        name: nameClean.length >= 2 ? nameClean : '',
      });
      if (abgebrochen) return;
      setGpLaedt(false);
      if (!res.ok) {
        setGpFehler(res.message || res.error || '');
        return;
      }
      const liste = Array.isArray(res.treffer) ? res.treffer : [];
      setGpTrefferListe(liste);
      if (res.eindeutig && liste[0]) {
        setGpAusgewaehlt(liste[0]);
        if (liste[0].kundenNr && !kdClean) {
          setGpKundenNr(liste[0].kundenNr);
        }
        if (setNameRef.current) {
          const basisName = liste[0].name || nameClean || '';
          const mitKd = formatGrenzpaketName(basisName, liste[0].kundenNr);
          if (mitKd && mitKd !== String(name || '').trim()) {
            setNameRef.current(mitKd);
          }
        }
      } else {
        setGpAusgewaehlt((prev) => (prev && liste.some((k) => k.kundenNr === prev.kundenNr) ? prev : null));
      }
    }, 350);
    return () => {
      abgebrochen = true;
      clearTimeout(timer);
    };
  }, [aktiv, gpKundenNr, name]);

  const preprocessOcr = (ocrResult) => {
    if (!aktiv || !ocrResult) return { rawName: ocrResult?.name || '' };
    const gpExtracted = extractGrenzpaketFromLabel({
      name: ocrResult.name,
      streetAndNumber: ocrResult.street,
      postalAndCity: [ocrResult.postal, ocrResult.city].filter(Boolean).join(' '),
    });
    gpManualSelectRef.current = false;
    setGpKundenNr(gpExtracted.kundenNr || '');
    const baseName = gpExtracted.cleanName || ocrResult.name || '';
    return {
      rawName: gpExtracted.kundenNr ? formatGrenzpaketName(baseName, gpExtracted.kundenNr) : baseName,
    };
  };

  const applyBeforeSave = ({ finalName, finalStreet, finalNotizen }) => {
    const demoAktiv = isDemoModeActive();
    if (!aktiv && !demoAktiv) return { finalName, finalStreet, finalNotizen };
    const gpKdFinal = String(gpAusgewaehlt?.kundenNr || gpKundenNr || initialGpMeta?.kundenNr || '').replace(/\D+/g, '').trim();
    if (!aktiv && demoAktiv) {
      const raw = String(finalNotizen || '').trim();
      return {
        finalName,
        finalStreet,
        finalNotizen: raw.includes('[DEMO]') ? raw : (raw ? `${raw} [DEMO]` : '[DEMO]'),
      };
    }
    const rawNote = String(finalNotizen || '').trim();
    const existingGp = parseGrenzpaketMeta({ notizen: rawNote });
    const customUserInfo = (!existingGp && rawNote && !istZentraleBuchhaltungsLog(rawNote)) ? rawNote : '';
    const rawTarif = gpAusgewaehlt?.tarif || initialGpMeta?.tarif || 'Unbekannt';
    const aktPaketTyp = gpPaketTyp || 'Paket';
    const { tarif: tarifFinal, postboxTyp: postboxFinal } = getGrenzpaketTarifDetails({
      tarif: rawTarif,
      paketTyp: aktPaketTyp,
      postboxTyp: gpAusgewaehlt?.postboxTyp || initialGpMeta?.postboxTyp || '',
    });
    const existingPaketCode = initialGpMeta?.paketCode || existingGp?.paketCode || '';
    const demoPaketCode = existingPaketCode || `${tarifFinal === 'Bar' ? 'B' : 'K'}09${String(Date.now()).slice(-6)}`;
    const cleanShopInfo = String(gpShopInfo || customUserInfo || initialGpMeta?.info || '').trim();
    const gpMeta = {
      kundenNr: gpKdFinal,
      tarif: tarifFinal,
      postboxTyp: postboxFinal,
      guthaben: gpAusgewaehlt?.guthaben ?? initialGpMeta?.guthaben ?? null,
      paketTyp: aktPaketTyp,
      logId: initialGpMeta?.logId || '',
      paketCode: demoPaketCode,
      info: istZentraleBuchhaltungsLog(cleanShopInfo) ? '' : cleanShopInfo,
      infoRaw: initialGpMeta?.infoRaw || (istZentraleBuchhaltungsLog(cleanShopInfo) ? '' : cleanShopInfo),
    };
    const nextNotizen = buildGrenzpaketNote(gpMeta);
    let nextStreet = finalStreet;
    if (istGrenzpaketShopStrasse(nextStreet) || /^KD\s*\d+/i.test(String(nextStreet || '').trim())) {
      nextStreet = formatGrenzpaketStreet(gpMeta);
    }
    const nextName = gpKdFinal
      ? formatGrenzpaketName(finalName || gpAusgewaehlt?.name || '', gpKdFinal)
      : finalName;
    // Wie in delivery_service.php ("ACHTUNG! Nach Eingabe/Scan ist wieder 'Paket-Nachricht' aktiv!"):
    // Nach dem Speichern eines neuen Pakets automatisch wieder auf 'Paket' zurücksetzen, damit im Serienmodus
    // nicht versehentlich das nächste Paket als Brief/PaketKlein gebucht wird.
    if (!initialName && aktPaketTyp !== 'Paket') {
      setTimeout(() => setGpPaketTyp('Paket'), 0);
    }
    return { finalName: nextName, finalStreet: nextStreet, finalNotizen: nextNotizen };
  };

  const renderSlot = ({ colors = {}, lang = 'de' } = {}) => {
    if (!aktiv) return null;
    const g = gt(lang);
    const dark = !!colors.darkMode;
    const tarifDetails = getGrenzpaketTarifDetails({
      tarif: gpAusgewaehlt?.tarif || 'Unbekannt',
      paketTyp: gpPaketTyp || 'Paket',
      postboxTyp: gpAusgewaehlt?.postboxTyp || '',
      guthaben: gpAusgewaehlt?.guthaben ?? null,
    });
    const isBar = tarifDetails.tarif === 'Bar';
    const waehleKunde = (k) => {
      if (!k) return;
      onStopSeries?.();
      gpManualSelectRef.current = true;
      setGpAusgewaehlt(k);
      if (k.kundenNr) setGpKundenNr(k.kundenNr);
      if (k.name && setName) {
        setName(formatGrenzpaketName(k.name, k.kundenNr));
        onClearNameInvalid?.();
      }
    };

    return (
      <div
        data-testid="grenzpaket-modal-card"
        style={{
          marginTop: 8,
          marginBottom: 6,
          padding: '10px 12px',
          borderRadius: 10,
          border: `1.5px solid ${
            gpAusgewaehlt
              ? isBar
                ? '#f59e0b'
                : '#10b981'
              : gpTrefferListe.length > 1
                ? '#3b82f6'
                : dark
                  ? '#374151'
                  : '#cbd5e1'
          }`,
          background: gpAusgewaehlt
            ? isBar
              ? dark ? '#3a2a10' : '#fffbeb'
              : dark ? '#0f291e' : '#ecfdf5'
            : dark
              ? '#1b2028'
              : '#f8fafc',
          fontSize: 12.5,
          color: colors.text,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <strong style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>🇨🇭</span>
            <span>{g.customerMatch}</span>
          </strong>
          {gpLaedt ? (
            <span style={{ fontSize: 11.5, color: colors.textSecondary || '#888' }}>⏳ {g.customerSearching}</span>
          ) : gpAusgewaehlt ? (
            <span
              data-testid="gp-matched-badge"
              style={{
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                background: isBar ? '#fef3c7' : '#d1fae5',
                color: isBar ? '#92400e' : '#065f46',
              }}
            >
              ✅ KD {gpAusgewaehlt.kundenNr} · {isBar ? `${g.tarifBar} (${tarifDetails.gebuehrText})` : `💳 ${gpAusgewaehlt.postboxTyp || g.tarifKredit}`}
            </span>
          ) : gpTrefferListe.length > 1 ? (
            <span style={{ fontSize: 11.5, fontWeight: 700, color: '#2563eb' }}>
              ⚠️ {gpTrefferListe.length} Treffer — bitte auswählen
            </span>
          ) : (String(name || '').trim().length >= 2 || String(gpKundenNr || '').trim().length >= 3) ? (
            <span style={{ fontSize: 11.5, color: '#d97706' }}>❓ {g.customerNone}</span>
          ) : null}
        </div>

        {/* KD-Nr. Eingabe + Pakettyp-Umschalter */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 135px' }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: colors.textSecondary || '#64748b', whiteSpace: 'nowrap' }}>
              {g.customerNo}:
            </span>
            <input
              type="text"
              inputMode="numeric"
              data-testid="gp-kdnr-input"
              value={gpKundenNr}
              onChange={(e) => {
                onStopSeries?.();
                gpManualSelectRef.current = false;
                setGpKundenNr(e.target.value.replace(/[^\d]/g, '').slice(0, 8));
              }}
              placeholder="z.B. 200086"
              style={{
                width: '100%',
                padding: '5px 8px',
                borderRadius: 6,
                border: `1px solid ${colors.border || '#cbd5e1'}`,
                background: dark ? '#111827' : '#ffffff',
                color: colors.text,
                fontSize: 13,
                fontWeight: 700,
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { id: 'Paket', label: `📦 ${g.typePaket}`, hint: 'Bar 6€ / Kredit 1,0' },
              { id: 'PaketKlein', label: '✉️ Päckchen (≤35cm)', hint: 'Bar 3€ / Kredit 0,5' },
              { id: 'Brief', label: `📄 ${g.typeBrief} (1€ bar)`, hint: 'Immer 1€ / 1 CHF bar' },
            ].map((tOpt) => {
              const istTyp = gpPaketTyp === tOpt.id;
              return (
                <button
                  key={tOpt.id}
                  type="button"
                  title={tOpt.hint}
                  data-testid={`gp-type-${tOpt.id}`}
                  onClick={() => { onStopSeries?.(); setGpPaketTyp(tOpt.id); }}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    fontSize: 11.5,
                    fontWeight: istTyp ? 700 : 600,
                    cursor: 'pointer',
                    border: `1px solid ${istTyp ? '#dc2626' : (colors.border || '#cbd5e1')}`,
                    background: istTyp ? '#dc2626' : (dark ? '#22272e' : '#ffffff'),
                    color: istTyp ? '#ffffff' : colors.text,
                  }}
                >
                  {tOpt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Ausgewählter Kunde Details */}
        {gpAusgewaehlt && (
          <div
            data-testid="gp-selected-customer"
            style={{
              padding: '6px 9px',
              borderRadius: 8,
              background: dark ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.85)',
              border: `1px solid ${isBar ? '#fcd34d' : '#a7f3d0'}`,
              marginBottom: 6,
              lineHeight: 1.35,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>
                👤 {stripGrenzpaketKundenNrFromName(gpAusgewaehlt.name || name)} <span style={{ opacity: 0.75 }}>(KD {gpAusgewaehlt.kundenNr})</span>
              </span>
              <span style={{ fontWeight: 800, fontSize: 12, color: isBar ? '#b45309' : '#047857' }}>
                {gpPaketTyp === 'Brief'
                  ? '💶 BRIEF: 1,00 CHF / 1,00 EUR bar (immer Barzahlung!)'
                  : isBar
                    ? `💶 BARZAHLUNG: ${tarifDetails.gebuehrText} (${tarifDetails.bonTypName})`
                    : `💳 ${gpAusgewaehlt.postboxTyp || 'Paketfach'}${
                        gpAusgewaehlt.guthaben !== null && gpAusgewaehlt.guthaben !== undefined
                          ? ` · ${g.guthaben}: ${gpAusgewaehlt.guthaben} (−${gpPaketTyp === 'PaketKlein' ? '0,5' : '1,0'})`
                          : ''
                      }`}
              </span>
            </div>
          </div>
        )}

        {/* Optionale Shop-Notiz zum Paket (z. B. Sperrgut, Reifen, Thomm — ohne Buchhaltungs-Logs der Zentrale) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary || '#64748b', whiteSpace: 'nowrap' }}>
            📝 Shop-Info:
          </span>
          <input
            type="text"
            data-testid="gp-shop-info-input"
            value={gpShopInfo}
            onChange={(e) => {
              onStopSeries?.();
              setGpShopInfo(e.target.value.slice(0, 80));
            }}
            placeholder="Optional (z.B. Sperrgut, Reifen, Postschrank)"
            style={{
              flex: 1,
              padding: '4px 8px',
              borderRadius: 6,
              border: `1px solid ${colors.border || '#cbd5e1'}`,
              background: dark ? '#111827' : '#ffffff',
              color: colors.text,
              fontSize: 12,
            }}
          />
        </div>

        {/* Kandidaten zum Antippen */}
        {gpTrefferListe.length >= 1 && (!gpAusgewaehlt || gpTrefferListe.length > 1) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
            {gpTrefferListe.slice(0, 6).map((k) => {
              const istAktiv = gpAusgewaehlt?.kundenNr === k.kundenNr;
              const kBar = k.tarif === 'Bar';
              return (
                <button
                  key={k.kundenNr}
                  type="button"
                  data-testid={`gp-candidate-${k.kundenNr}`}
                  onClick={() => waehleKunde(k)}
                  style={{
                    padding: '5px 9px',
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: istAktiv ? 800 : 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    border: `1.5px solid ${istAktiv ? '#2563eb' : (colors.border || '#cbd5e1')}`,
                    background: istAktiv ? (dark ? '#1e3a8a' : '#dbeafe') : (dark ? '#111827' : '#ffffff'),
                    color: colors.text,
                  }}
                >
                  {k.name} · <strong>KD {k.kundenNr}</strong> · {kBar ? '💶 Bar' : `💳 ${k.postboxTyp || 'Kredit'}`}
                  {(k.ort || k.strasse) ? (
                    <span style={{ opacity: 0.8, fontWeight: 500 }}>
                      {' · 📍 '}{[k.plz, k.ort].filter(Boolean).join(' ')}{k.strasse ? `, ${k.strasse}` : ''}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        {gpFehler && (
          <div style={{ fontSize: 11.5, color: '#dc2626', marginBottom: 5 }}>⚠️ {gpFehler}</div>
        )}

        {/* 🧪 Demo-/Trockenlauf-Buchungshinweis */}
        <div
          data-testid="gp-modal-demo-intake-info"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '6px 10px',
            borderRadius: 7,
            border: `1px solid ${dark ? '#1d4ed8' : '#93c5fd'}`,
            background: dark ? 'rgba(30, 58, 138, 0.35)' : '#eff6ff',
            color: dark ? '#bfdbfe' : '#1e40af',
            fontSize: 11.5,
            fontWeight: 600,
            textAlign: 'left',
            lineHeight: 1.35,
          }}
        >
          🧪 <strong>Demo-Buchung aktiv:</strong> Beim Speichern wird der Grenzpaket-Paketcode erzeugt, das Quittungs-Etikett gedruckt & alle Felder im Prüf-Log protokolliert (keine E-Mail an den Kunden).
        </div>
      </div>
    );
  };

  return {
    allowEmptyShelf: aktiv,
    // Bei Grenzpaket nie den hektischen 3-Sekunden-Auto-Save-Countdown starten:
    // Der Nutzer behält volle Ruhe beim Prüfen; wenn Serienmodus an ist, öffnet sich nach Tipp auf „Speichern" trotzdem sofort die Kamera fürs nächste Paket!
    isBlockingAutoSave: aktiv,
    preprocessOcr,
    applyBeforeSave,
    renderSlot,
  };
}

export function GrenzpaketPickupSlot({ paket, colors, lang = 'de' }) {
  const gpMeta = parseGrenzpaketMeta(paket);
  if (!gpMeta) return null;
  const dark = !!colors?.darkMode;
  const tr = makeTr(lang);
  const c = {
    text: dark ? '#ffffff' : '#1f2328',
    sub: dark ? '#9ca3af' : '#57606a',
    border: dark ? '#374151' : '#d0d7de',
  };
  const tarifDetails = getGrenzpaketTarifDetails(gpMeta);
  const isBar = tarifDetails.tarif === 'Bar';
  const isKredit = tarifDetails.tarif === 'Kredit';
  const warnFarbe = !isBar && !isKredit;

  return (
    <div
      data-testid="signature-grenzpaket-banner"
      style={{
        padding: '10px 12px',
        borderRadius: 10,
        marginBottom: 12,
        border: `2px solid ${isBar || warnFarbe ? '#f59e0b' : '#10b981'}`,
        background: isBar || warnFarbe
          ? dark ? '#3a2a10' : '#fffbeb'
          : dark ? '#0f291e' : '#ecfdf5',
        color: c.text,
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <strong style={{ fontSize: 13.5 }}>
          🇨🇭 Grenzpaket {gpMeta.kundenNr ? `· KD ${gpMeta.kundenNr}` : ''}
        </strong>
        <span style={{ fontSize: 12, opacity: 0.85, fontWeight: 600 }}>
          📦 {tarifDetails.bonTypName}{gpMeta.paketCode ? ` · ${gpMeta.paketCode}` : ''}
        </span>
      </div>
      <div
        data-testid="signature-grenzpaket-tarif"
        style={{
          fontWeight: 800,
          fontSize: 14,
          color: isBar || warnFarbe ? (dark ? '#fcd34d' : '#b45309') : (dark ? '#6ee7b7' : '#047857'),
          marginBottom: gpMeta.info ? 4 : 8,
        }}
      >
        {isBar
          ? `💶 ${tr(
              `BARZAHLUNG (${tarifDetails.bonTypName}): Bitte ${tarifDetails.gebuehrText} beim Kunden kassieren!`,
              `CASH PAYMENT (${tarifDetails.bonTypName}): Please collect ${tarifDetails.gebuehrText} from customer!`,
              `CONTANTE BETALING (${tarifDetails.bonTypName}): ${tarifDetails.gebuehrText} bij klant innen!`
            )}`
          : isKredit
            ? `💳 ${tr('Paketfach-Guthaben', 'Postbox credit', 'Pakketvak-tegoed')} (${tarifDetails.postboxTyp || 'Kredit'} · ${tarifDetails.gebuehrText})${
                gpMeta.guthaben !== null && gpMeta.guthaben !== undefined ? ` · ${tr('Guthaben', 'Credit', 'Tegoed')}: ${gpMeta.guthaben}` : ''
              }`
            : `⚠️ ${tr('TARIF UNBEKANNT — Bitte prüfen, ob Barzahlung kassiert werden muss!', 'TARIFF UNKNOWN — Please check if cash payment is required!', 'TARIEF ONBEKEND — Controleer of contante betaling nodig is!')}`}
      </div>
      {gpMeta.info && !istZentraleBuchhaltungsLog(gpMeta.info) && (
        <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginBottom: 6 }}>
          📌 Info / Lager: {gpMeta.info}{paket?.regalplatz ? ` · Lager: ${paket.regalplatz}` : ''}
        </div>
      )}
      {paket?.trackingNumber && paket.trackingNumber !== gpMeta.paketCode && (
        <div style={{ fontSize: 12, opacity: 0.9, fontFamily: 'monospace', marginBottom: 8 }}>
          🔢 Sendungsnr.: {paket.trackingNumber}
        </div>
      )}
      <div
        data-testid="signature-grenzpaket-demo-info"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '7px 10px',
          borderRadius: 8,
          border: `1px solid ${dark ? '#1d4ed8' : '#93c5fd'}`,
          background: dark ? 'rgba(30, 58, 138, 0.35)' : '#eff6ff',
          color: dark ? '#bfdbfe' : '#1e40af',
          fontSize: 11.5,
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        🧪 <strong>{tr('Demo-Ausbuchung aktiv:', 'Demo checkout active:', 'Demo-uitboeking actief:')}</strong>{' '}
        {tr(
          'Beim Bestätigen der Unterschrift wird die komplette Grenzpaket-Ausbuchung (KD-Nr., Paket-Code, Tarif & Unterschrift) im Prüf-Log protokolliert.',
          'Confirming the signature logs the complete Grenzpaket checkout payload in the inspection log.',
          'Bij bevestigen wordt de volledige Grenzpaket-uitboeking in het controlelog geregistreerd.'
        )}
      </div>
    </div>
  );
}

export const grenzpaketModule = {
  id: 'grenzpaket',
  testIdOpen: 'grenzpaket-open',
  isAvailable: () => grenzpaketFreigeschaltet(),
  isActive: () => grenzpaketAktiv(),
  getMenuLabel: (lang = 'de') => `${gt(lang)('menu')} →`,
  PickupSlot: GrenzpaketPickupSlot,
  SettingsModal: GrenzpaketPanel,
  matchesSearch: (paket, searchLower) => {
    if (!paket || !searchLower) return false;
    const gp = parseGrenzpaketMeta(paket);
    if (gp) {
      const hay = [gp.kundenNr, gp.tarif, gp.postboxTyp, gp.paketTyp, gp.paketCode, gp.info, gp.infoRaw]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (hay.includes(searchLower)) return true;
      const words = String(searchLower).split(/\s+/).filter(Boolean);
      if (words.length > 1 && words.every((w) => hay.includes(w))) return true;
    }
    const notiz = String(paket.notizen || '').toLowerCase();
    if (!notiz.includes('grenzpaket') && !notiz.includes('"gp":')) return false;
    return notiz.includes(searchLower);
  },
};

